import type { ApolloClient } from "@apollo/client";
import {
  ApplyOrganizationalPowerAnalysisDocument,
  OrganizationalPowerAnalysisDocument,
  StartOrganizationalPowerAnalysisDocument,
  type OrganizationalPowerAnalysisResultFragment,
} from "@/graphql/generated/operations";
import { OrganizationalPowerAnalysisStatus } from "@/graphql/generated/types";

export type OrganizationalPowerAnalysis = OrganizationalPowerAnalysisResultFragment;

/** Which record and person an analysis must belong to. Every answer is checked against it. */
export type OrganizationalPowerScope = { recordId: string; personId: string };

const DEFAULT_POLL_INTERVAL_MS = 2_000;

function inScope(value: OrganizationalPowerAnalysis | null | undefined, scope: OrganizationalPowerScope): OrganizationalPowerAnalysis {
  if (!value || value.recordId !== scope.recordId || value.subjectPersonId !== scope.personId) {
    throw new Error("Unexpected analysis scope");
  }
  return value;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException("Aborted", "AbortError"));
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

// Proposals hold numbers derived from private record documents: no Apollo cache on any call
// (same rule as OrganizationalPowerAnalysisDialog).

export async function startOrganizationalPowerAnalysis(
  client: ApolloClient,
  scope: OrganizationalPowerScope,
  documentIds: readonly string[],
): Promise<OrganizationalPowerAnalysis> {
  const { data } = await client.mutate({
    mutation: StartOrganizationalPowerAnalysisDocument,
    variables: { recordId: scope.recordId, documentIds: [...documentIds] },
    fetchPolicy: "no-cache",
  });
  return inScope(data?.startOrganizationalPowerAnalysis, scope);
}

/**
 * Reads the analysis until it leaves RUNNING. Genesis turns a run past its hard timeout into
 * FAILED on the next read, so the loop always ends; `signal` stops watching, not the analysis.
 */
export async function awaitOrganizationalPowerAnalysis(
  client: ApolloClient,
  analysis: OrganizationalPowerAnalysis,
  scope: OrganizationalPowerScope,
  opts: { signal?: AbortSignal; pollIntervalMs?: number } = {},
): Promise<OrganizationalPowerAnalysis> {
  let current = analysis;
  while (current.status === OrganizationalPowerAnalysisStatus.Running) {
    await sleep(opts.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS, opts.signal);
    const { data } = await client.query({ query: OrganizationalPowerAnalysisDocument, variables: { id: analysis.id }, fetchPolicy: "no-cache" });
    // An answer that arrives after the caller stopped watching is not theirs to see.
    opts.signal?.throwIfAborted();
    current = inScope(data?.organizationalPowerAnalysis, scope);
  }
  return current;
}

/**
 * Transfers a reviewed proposal into the person's profile, a separate user action
 * (@external-adr GENESIS-0048-organizational-power-proposals-cross-into-profiles-only-on-apply).
 * Repeating it after a committed apply returns the applied proposal without writing again.
 */
export async function applyOrganizationalPowerAnalysis(
  client: ApolloClient,
  id: string,
  scope: OrganizationalPowerScope,
): Promise<OrganizationalPowerAnalysis> {
  const { data } = await client.mutate({ mutation: ApplyOrganizationalPowerAnalysisDocument, variables: { id }, fetchPolicy: "no-cache" });
  const value = inScope(data?.applyOrganizationalPowerAnalysis, scope);
  if (value.status !== OrganizationalPowerAnalysisStatus.Applied) throw new Error("Analysis application was not confirmed");
  client.cache.evict({ id: client.cache.identify({ __typename: "Person", id: scope.personId }), fieldName: "smartAnalysisResults" });
  void client.refetchQueries({ include: ["PersonProfile", "PowersData"] }).catch(() => undefined);
  return value;
}
